import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "../lib/api";
import "./QRScanner.css";
import { AuthImage } from "../lib/authFile";

/**
 * QRスキャン打刻端末（管理者向け）
 *
 * 動作フロー:
 *   1. 社員が社員証画面のQRをかざす
 *   2. カメラがスキャン → 自動で /worktime/punch/ を呼び出し
 *   3. 出勤 or 退勤の結果を大きく表示（社員情報も表示）
 *   4. 4秒後に自動でスキャン再開（連続打刻対応）
 *
 * 打刻データは WorkTimeRecord に記録され、
 * 管理側「打刻管理」画面でそのまま確認できる。
 */
const READER_ID  = "qr-reader-container";
const AUTO_RESET = 4000; // 打刻後に自動リセットするまでのミリ秒

const QRScanner = () => {
  const navigate      = useNavigate();
  const html5QrRef    = useRef(null);
  const autoResetRef  = useRef(null);

  const [scanning,   setScanning]   = useState(false);
  const [processing, setProcessing] = useState(false); // スキャン→打刻中
  const [result,     setResult]     = useState(null);  // 打刻結果
  const [employee,   setEmployee]   = useState(null);  // 社員情報
  const [displayCfg, setDisplayCfg] = useState(null);
  const [error,      setError]      = useState("");
  const [countdown,  setCountdown]  = useState(0);     // 自動リセットカウントダウン

  // ── カメラ停止 ──────────────────────────────────────────────────────────────
  const stopScanner = useCallback(() => {
    if (html5QrRef.current) {
      html5QrRef.current.stop()
        .catch(() => {})
        .finally(() => {
          try { html5QrRef.current.clear(); } catch (_) {}
          html5QrRef.current = null;
          setScanning(false);
        });
    }
  }, []);

  // ── 状態リセット＋スキャン再開 ─────────────────────────────────────────────
  const resetAndRestart = useCallback(() => {
    clearInterval(autoResetRef.current);
    setResult(null);
    setEmployee(null);
    setDisplayCfg(null);
    setError("");
    setCountdown(0);
    setProcessing(false);
  }, []);

  // ── カメラ起動 ──────────────────────────────────────────────────────────────
  const startScanner = useCallback(async () => {
    if (html5QrRef.current || processing) return;
    setError("");

    const html5Qr = new Html5Qrcode(READER_ID);
    html5QrRef.current = html5Qr;
    setScanning(true);

    try {
      await html5Qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          stopScanner();
          handleQRScanned(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error("カメラ起動エラー:", err);
      html5QrRef.current = null;
      setScanning(false);
      setError("カメラへのアクセスが拒否されました。ブラウザのカメラ権限を確認してください。");
    }
  }, [stopScanner, processing]); 

  // ── QRスキャン後の処理（社員情報取得 + 自動打刻） ──────────────────────────
  const handleQRScanned = useCallback(async (qrText) => {
    setProcessing(true);
    setError("");
    setResult(null);
    setEmployee(null);

    // 社員情報取得 と 打刻 を並列実行
    const [scanRes, punchRes] = await Promise.allSettled([
      api.post("/employees/scan-qr/", { payload: qrText }),
      api.post("/worktime/punch/",    { payload: qrText }),
    ]);

    // 社員情報
    if (scanRes.status === "fulfilled") {
      setEmployee(scanRes.value.data.employee || null);
      setDisplayCfg(scanRes.value.data.display_config || null);
    }

    // 打刻結果
    if (punchRes.status === "fulfilled") {
      const d      = punchRes.value.data;
      const action = d.action === "in" ? "出勤" : "退勤";
      const rawTime = d.start_time || d.end_time;
      const timeStr = rawTime
        ? new Date(rawTime).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })
        : "";
      setResult({ action, timeStr, name: d.name || "" });
    } else {
      const errData = punchRes.reason?.response?.data;
      const msg = errData?.error || errData?.detail || "打刻に失敗しました。";
      // すでに打刻済みの場合は特別メッセージ
      if (punchRes.reason?.response?.status === 400 && errData?.code === "already_completed") {
        setError("本日分の打刻は既に完了しています。");
      } else {
        setError(msg);
      }
    }

    setProcessing(false);

    // 4秒後に自動リセット＋スキャン再開
    let sec = Math.floor(AUTO_RESET / 1000);
    setCountdown(sec);
    autoResetRef.current = setInterval(() => {
      sec -= 1;
      setCountdown(sec);
      if (sec <= 0) {
        clearInterval(autoResetRef.current);
        resetAndRestart();
        // 少し待ってからカメラ再起動
        setTimeout(() => startScanner(), 300);
      }
    }, 1000);
  }, [resetAndRestart]);

  // ── USB スキャナー / 手動入力 ───────────────────────────────────────────────
  const handleManualInput = (e) => {
    e.preventDefault();
    const val = e.target.elements.qrInput.value.trim();
    if (val) {
      stopScanner();
      handleQRScanned(val);
      e.target.reset();
    }
  };

  // アンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      stopScanner();
      clearInterval(autoResetRef.current);
    };
  }, [stopScanner]);

  const cfg = displayCfg || {};

  // 打刻結果のスタイル
  const resultColor = result?.action === "出勤" ? "#1565C0" : "#B71C1C";
  const resultBg    = result?.action === "出勤" ? "#E3F2FD" : "#FFEBEE";
  const resultIcon  = result?.action === "出勤" ? "🟢" : "🔴";

  return (
    <div className="qrs-page">
      {/* ヘッダー */}
      <div className="qrs-header">
        <button
          className="qrs-btn-back"
          onClick={() => { stopScanner(); clearInterval(autoResetRef.current); navigate("/admin/qr"); }}
        >
          戻る
        </button>
        <h2 className="qrs-title">QRスキャン打刻</h2>
        <div style={{ width: 60 }} />
      </div>

      <div className="qrs-body">

        {/* ── 打刻結果カード（スキャン後に表示） ── */}
        {(result || error) && !processing ? (
          <div className="qrs-result-wrap">

            {result && (
              <div className="qrs-result-card" style={{ background: resultBg, borderColor: resultColor }}>
                <div className="qrs-result-icon">{resultIcon}</div>
                <div className="qrs-result-action" style={{ color: resultColor }}>
                  {result.action}しました
                </div>
                <div className="qrs-result-time">{result.timeStr}</div>
                {result.name && <div className="qrs-result-name">{result.name}</div>}
              </div>
            )}

            {error && <p className="qrs-error-msg">{error}</p>}

            {/* 社員情報（表示設定に従う） */}
            {employee && (
              <div className="qrs-employee-card">
                {cfg.show_photo && employee.photo_api_url && (
                  <div className="qrs-photo-wrap">
                    {/* 認証付きの配信 API から取得する（docs/spec_file_access.md） */}
                    <AuthImage url={employee.photo_api_url} alt="社員写真" className="qrs-photo" />
                  </div>
                )}
                <table className="qrs-info-table">
                  <tbody>
                    {cfg.show_memberId !== false && (
                      <tr><th>社員番号</th><td>{employee.memberId}</td></tr>
                    )}
                    {cfg.show_name !== false && (
                      <tr><th>氏名</th><td>{employee.name}</td></tr>
                    )}
                    {cfg.show_dept !== false && (
                      <tr><th>部署</th><td>{employee.departmentName || "—"}</td></tr>
                    )}
                    {cfg.show_position !== false && (
                      <tr><th>役職</th><td>{employee.position || "—"}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* 自動リセットカウントダウン + 今すぐ次へ */}
            <div className="qrs-auto-reset">
              <span className="qrs-countdown">{countdown}秒後に自動でスキャン再開</span>
              <button
                className="qrs-btn-rescan"
                onClick={() => {
                  clearInterval(autoResetRef.current);
                  resetAndRestart();
                  setTimeout(() => startScanner(), 200);
                }}
              >
                今すぐ次の社員へ
              </button>
            </div>
          </div>
        ) : (
          /* ── スキャンエリア（打刻前） ── */
          <div className="qrs-scan-area">
            <div className="qrs-camera-section">
              <div id={READER_ID} className="qrs-reader-box" />

              {processing ? (
                <div className="qrs-processing">⏳ 打刻処理中...</div>
              ) : (
                <div className="qrs-camera-btns">
                  {!scanning ? (
                    <button className="qrs-btn-primary" onClick={startScanner}>
                      📷 カメラでスキャン開始
                    </button>
                  ) : (
                    <>
                      <div className="qrs-scanning-label">🔍 QRコードをかざしてください</div>
                      <button className="qrs-btn-stop" onClick={stopScanner}>
                        ■ 停止
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* USB スキャナー / 手動入力 */}
            <details className="qrs-manual">
              <summary>テキスト入力（USB スキャナー対応）</summary>
              <form onSubmit={handleManualInput} className="qrs-manual-form">
                <input
                  name="qrInput"
                  type="text"
                  className="qrs-manual-input"
                  placeholder="QRデータをスキャナーで読み込み、またはここに入力..."
                  autoComplete="off"
                  autoFocus
                />
                <button type="submit" className="qrs-btn-primary">打刻</button>
              </form>
            </details>

            {error && <p className="qrs-error-msg">{error}</p>}

            <p className="qrs-hint">
              社員証画面のQRコードをカメラにかざすと、<br />
              自動で出勤・退勤が記録されます。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScanner;
